/* ═══════════════════════════════════════════════════════════════
   ProjectFormPanel — the full project form (create + edit), now a
   kit Sheet instead of an inline full-page section. Every field from
   the old combined Projects page's detail form is preserved; only the chrome
   (Sheet vs. inline page) changed.
   ═══════════════════════════════════════════════════════════════ */
import React, { useEffect, useRef, useState } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from '../../components/ui/sheet';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { SearchableSelect } from '../../components/SearchableSelect';
import { isWeb } from '../../lib/platform';
import { rem } from '../../lib/rem';
import type {
  Project,
  Organization,
  ProjectType,
  BillingType,
  ProjectPhase,
  TeamMember,
  ProjectScope,
  ProjectScopeAssignment,
} from '../../../shared/types';
import { labelStyle, inputStyle } from './styles';
import {
  saveProject as saveProjectMutation,
  toggleScope as toggleScopeMutation,
  setCurrentPhase,
} from './projectMutations';
import type { FirmPhaseDef } from './widgets/context';

export interface ProjectFormPanelProps {
  open: boolean;
  mode: 'create' | 'edit';
  project: Project;
  orgs: Organization[];
  projectTypes: ProjectType[];
  billingTypes: BillingType[];
  members: TeamMember[];
  firmPhases: FirmPhaseDef[];
  allScopes: ProjectScope[];
  scopeAssignments: ProjectScopeAssignment[];
  phases: ProjectPhase[];
  onSaved: (project: Project) => void;
  onClose: () => void;
  toast: (msg: string, isError?: boolean) => void;
  /** Gates the Contract Value field (projects.view_financials). When false,
   *  the field is hidden AND the save payload never carries a contractValue
   *  edit — draft always spreads the original project's value through, so a
   *  user without the permission can never clobber it via this panel. */
  canViewFinancials: boolean;
  /** Gates the "Closed" status option (projects.archive) and the close
   *  confirm on Save. Defaults false — callers that never offer Closed
   *  here (e.g. the New Project panel, always `mode: 'create'`) can omit
   *  it. */
  canArchiveProject?: boolean;
  /** Override the save call (default: the module-level `saveProjectMutation`).
   *  A dashboard widget (T4b Details → Manage) passes `ctx.saveProject` here
   *  instead so the save goes through the host's own mutation — same DB
   *  write, but it also updates `ctx.project` and reloads the dashboard's
   *  assignments/phases slices, which a bare `saveProjectMutation` call
   *  never touches. */
  onSave?: (project: Project) => Promise<Project>;
  /** Current-phase picks commit immediately to the DB (bypassing Save/
   *  Cancel), same as the old inline behavior — but that write happens
   *  outside `ctx`, so the host's own project/phases state goes stale.
   *  Called once on close (Save OR Cancel) with just the phase fields that
   *  changed, so a caller with a dashboard `ctx` (Details → Manage) can
   *  resync `ctx.project` + reload `ctx.phases` without resaving any of
   *  this panel's other (possibly discarded) field edits. */
  onPhaseCommitted?: (patch: { currentPhaseId?: string | null }) => void;
}

export function ProjectFormPanel({
  open,
  mode,
  project,
  orgs,
  projectTypes,
  billingTypes,
  members,
  firmPhases,
  allScopes,
  scopeAssignments,
  phases,
  onSaved,
  onClose,
  toast,
  onSave,
  canViewFinancials,
  canArchiveProject = false,
  onPhaseCommitted,
}: ProjectFormPanelProps) {
  const [draft, setDraft] = useState<Project>(project);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [confirmCloseSave, setConfirmCloseSave] = useState(false);
  const [scopeAssign, setScopeAssign] = useState<ProjectScopeAssignment[]>(scopeAssignments);
  const [localPhases, setLocalPhases] = useState<ProjectPhase[]>(phases);
  const [phasePatch, setPhasePatch] = useState<{ currentPhaseId?: string | null } | null>(null);

  // Reset the draft every time the panel opens on a (possibly different)
  // project — never while it's open and the user is mid-edit.
  useEffect(() => {
    if (open) {
      setDraft(JSON.parse(JSON.stringify(project)));
      setScopeAssign(scopeAssignments);
      setLocalPhases(phases);
      setDirty(false);
      setPhasePatch(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, project.id]);

  // Radix's Dialog only restores focus to a <Dialog.Trigger> ref on close —
  // this kit's Sheets are always opened imperatively from a page-owned
  // button, never a <SheetTrigger>, so that ref is always null and focus
  // silently lands on <body> (DESIGN.md §7). Capture whatever was focused
  // right before open and restore it ourselves via onCloseAutoFocus.
  const openerRef = useRef<Element | null>(null);
  useEffect(() => {
    if (open) openerRef.current = document.activeElement;
  }, [open]);

  /** Closes the sheet and, if the current phase was picked during this
   *  visit, hands the committed patch to the caller so it can resync its
   *  own state (see `onPhaseCommitted` doc comment). */
  function closeAndNotify() {
    if (phasePatch && onPhaseCommitted) onPhaseCommitted(phasePatch);
    onClose();
  }

  function update(patch: Partial<Project>) {
    setDraft((prev) => ({ ...prev, ...patch }));
    setDirty(true);
  }

  function requestClose() {
    if (dirty) setConfirmDiscard(true);
    else closeAndNotify();
  }

  async function handleToggleScope(scopeId: string) {
    if (!draft.id) return;
    const updated = await toggleScopeMutation(draft.id, scopeId);
    setScopeAssign(updated);
  }

  async function handleCurrentPhaseChange(phaseId: string) {
    if (!phaseId) return;
    const result = await setCurrentPhase(draft, phaseId);
    setDraft((prev) => ({ ...prev, currentPhaseId: result.project.currentPhaseId }));
    setPhasePatch({ currentPhaseId: result.project.currentPhaseId });
  }

  async function doSave() {
    setSaving(true);
    try {
      const saved = onSave
        ? await onSave(draft)
        : (await saveProjectMutation(draft, firmPhases)).project;
      toast('Project saved');
      setDirty(false);
      onSaved(saved);
      // `onSave`/`saveProjectMutation` already persisted + notified the
      // phase along with everything else in `draft` — skip the redundant
      // onPhaseCommitted write.
      setPhasePatch(null);
      setConfirmCloseSave(false);
      closeAndNotify();
    } catch (err: any) {
      // When `onSave` is supplied, the caller's own mutation already toasted
      // the failure (e.g. Dashboard's ctx.saveProject → withToast) — skip
      // the duplicate.
      if (!onSave) toast('Save failed: ' + (err.message || 'Unknown error'), true);
      setConfirmCloseSave(false);
    } finally {
      setSaving(false);
    }
  }

  function handleSave() {
    if (!draft.name.trim()) {
      toast('Enter a project name', true);
      return;
    }
    // Active → Closed is a destructive, firm-wide lock — same confirm the
    // header's "Close project" action uses (Dashboard.tsx). Any other
    // status transition (or no status change) saves straight through.
    if (canArchiveProject && draft.status === 'closed' && project.status !== 'closed') {
      setConfirmCloseSave(true);
      return;
    }
    void doSave();
  }

  const gridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: 'var(--space-3)',
  };

  return (
    <>
      <Sheet open={open} onOpenChange={(o) => !o && requestClose()}>
        <SheetContent
          side="right"
          className="!w-[clamp(26rem,40vw,44rem)]"
          onCloseAutoFocus={(e) => {
            e.preventDefault();
            if (openerRef.current instanceof HTMLElement) openerRef.current.focus();
          }}
        >
          <SheetHeader>
            <SheetTitle>{mode === 'create' ? 'New project' : 'Edit project'}</SheetTitle>
          </SheetHeader>

          <div style={{ ...gridStyle, marginTop: 'var(--space-4)', flex: 1, overflowY: 'auto' }}>
            <div>
              <Label htmlFor="pf-number">Project number</Label>
              <input
                id="pf-number"
                value={draft.projectNumber || ''}
                onChange={(e) => update({ projectNumber: e.target.value })}
                placeholder="e.g. 2026-001"
                style={{ ...inputStyle, fontFamily: "'JetBrains Mono', monospace" }}
              />
            </div>

            <div>
              <Label>Status</Label>
              <SearchableSelect
                value={draft.status || 'active'}
                options={[
                  { value: 'active', label: 'Active' },
                  ...(canArchiveProject ? [{ value: 'closed', label: 'Closed' }] : []),
                ]}
                onChange={(v) => update({ status: (v || 'active') as Project['status'] })}
                placeholder="Select status"
                clearable={false}
              />
            </div>

            <div>
              <Label htmlFor="pf-name">Project name</Label>
              <input
                id="pf-name"
                value={draft.name}
                onChange={(e) => update({ name: e.target.value })}
                style={inputStyle}
                autoFocus
              />
            </div>

            <div>
              <Label>Client</Label>
              <SearchableSelect
                value={draft.clientOrgId || ''}
                options={(() => {
                  const clients = orgs.filter((o) => o.type === 'client' || o.type === 'both');
                  const others = orgs.filter((o) => o.type !== 'client' && o.type !== 'both');
                  return [
                    ...clients.map((o) => ({ value: o.id, label: o.name, group: 'Clients' })),
                    ...others.map((o) => ({ value: o.id, label: o.name, group: 'Other' })),
                  ];
                })()}
                onChange={(v) =>
                  update({
                    clientOrgId: v || undefined,
                    clientName: orgs.find((o) => o.id === v)?.name,
                  })
                }
                placeholder="Select client"
                searchPlaceholder="Search clients…"
              />
            </div>

            <div>
              <Label>Project type</Label>
              <SearchableSelect
                value={draft.projectTypeId || ''}
                options={projectTypes.map((t) => ({ value: t.id, label: t.name }))}
                onChange={(v) => update({ projectTypeId: v || undefined })}
                placeholder="Select type"
                searchPlaceholder="Search types…"
              />
            </div>

            <div>
              <Label>Fee structure</Label>
              <SearchableSelect
                value={draft.billingTypeId || ''}
                options={billingTypes.map((t) => ({ value: t.id, label: t.name }))}
                onChange={(v) => update({ billingTypeId: v || undefined })}
                placeholder={
                  billingTypes.length === 0 ? 'Add fee structures in Admin' : 'Select fee structure'
                }
                searchPlaceholder="Search fee structures…"
              />
            </div>

            <div>
              <Label>PIC (Principal-in-Charge)</Label>
              <SearchableSelect
                value={draft.picId || ''}
                options={members
                  .filter((m) => m.active)
                  .map((m) => ({ value: m.id, label: m.name, hint: m.role }))}
                onChange={(v) => update({ picId: v || undefined })}
                placeholder="Select PIC"
                searchPlaceholder="Search team…"
              />
            </div>

            <div>
              <Label>PM (Project Manager)</Label>
              <SearchableSelect
                value={draft.pmId || ''}
                options={members
                  .filter((m) => m.active)
                  .map((m) => ({ value: m.id, label: m.name, hint: m.role }))}
                onChange={(v) => update({ pmId: v || undefined })}
                placeholder="Select PM"
                searchPlaceholder="Search team…"
              />
            </div>

            {canViewFinancials && (
              <div>
                <Label htmlFor="pf-value">Contract value</Label>
                <input
                  id="pf-value"
                  type="number"
                  value={draft.contractValue ?? ''}
                  onChange={(e) =>
                    update({ contractValue: e.target.value ? Number(e.target.value) : undefined })
                  }
                  placeholder="0.00"
                  style={{ ...inputStyle, fontFamily: "'JetBrains Mono', monospace" }}
                />
              </div>
            )}

            {/* Anticipated Hours field intentionally omitted — see
                projectMutations.saveProject doc comment: `projects` has no
                anticipated_hours/-expenses columns, so a live field here
                would 500 on save. Flagged in PROJECT_CENTER_PARITY.md FORM11. */}

            {mode === 'edit' && draft.id && localPhases.length > 0 && (
              <div>
                <Label>Current phase</Label>
                <SearchableSelect
                  value={draft.currentPhaseId || ''}
                  options={localPhases
                    .slice()
                    .sort((a, b) => a.sortOrder - b.sortOrder)
                    .map((ph) => ({ value: ph.id, label: ph.name, hint: ph.status }))}
                  onChange={handleCurrentPhaseChange}
                  placeholder="Select phase"
                  clearable={false}
                />
              </div>
            )}

            {!isWeb && (
              <div>
                <div style={labelStyle}>Project folder</div>
                <div style={{ display: 'flex', gap: rem(6), alignItems: 'center' }}>
                  <input
                    value={draft.folderPath || ''}
                    readOnly
                    placeholder="No folder linked"
                    title={draft.folderPath || ''}
                    style={{
                      ...inputStyle,
                      flex: 1,
                      fontFamily: "'JetBrains Mono', monospace",
                      fontSize: 'var(--font-size-sm)',
                    }}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={async () => {
                      const dir = await (window as any).api?.selectFolderDialog?.();
                      if (dir) update({ folderPath: dir });
                    }}
                  >
                    Pick
                  </Button>
                  {draft.folderPath && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => (window as any).api?.openFolder?.(draft.folderPath)}
                    >
                      Open
                    </Button>
                  )}
                  {draft.folderPath && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => update({ folderPath: undefined })}
                    >
                      Clear
                    </Button>
                  )}
                </div>
              </div>
            )}

            {mode === 'edit' && draft.id && allScopes.length > 0 && (
              <div>
                <div style={labelStyle}>Scopes</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: rem(6) }}>
                  {allScopes
                    .filter((s) => !s.parentId)
                    .map((parent) => {
                      const children = allScopes.filter((s) => s.parentId === parent.id);
                      const items = children.length > 0 ? children : [parent];
                      const showParentCheckbox = children.length === 0;
                      return (
                        <div key={parent.id}>
                          {children.length > 0 && (
                            <div
                              style={{
                                fontSize: 'var(--font-size-sm)',
                                fontWeight: 700,
                                color: 'var(--tm)',
                                marginBottom: 'var(--space-1)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: rem(6),
                              }}
                            >
                              <span
                                style={{
                                  width: 'var(--space-2)',
                                  height: 'var(--space-2)',
                                  borderRadius: '50%',
                                  background: parent.color,
                                  flexShrink: 0,
                                }}
                              />
                              {parent.name}
                            </div>
                          )}
                          <div
                            style={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              gap: rem(6),
                              marginLeft: children.length > 0 ? rem(14) : 0,
                            }}
                          >
                            {(showParentCheckbox ? [parent] : items).map((scope) => {
                              const assigned = scopeAssign.some((a) => a.scopeId === scope.id);
                              return (
                                <button
                                  type="button"
                                  key={scope.id}
                                  onClick={() => handleToggleScope(scope.id)}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: rem(6),
                                    cursor: 'pointer',
                                    fontSize: 'var(--font-size-base)',
                                    padding: `var(--space-1) ${rem(10)}`,
                                    borderRadius: rem(5),
                                    background: assigned ? scope.color + '18' : 'var(--sa)',
                                    border: `1px solid ${assigned ? scope.color + '40' : 'var(--bd)'}`,
                                    minHeight: rem(32),
                                  }}
                                >
                                  <span
                                    style={{
                                      width: 'var(--space-2)',
                                      height: 'var(--space-2)',
                                      borderRadius: '50%',
                                      background: scope.color,
                                      flexShrink: 0,
                                    }}
                                  />
                                  <span
                                    style={{
                                      fontWeight: assigned ? 600 : 400,
                                      color: assigned ? scope.color : 'var(--tx)',
                                    }}
                                  >
                                    {scope.name}
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            )}
          </div>

          <SheetFooter>
            <Button variant="outline" onClick={requestClose} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <ConfirmDialog
        open={confirmDiscard}
        onOpenChange={setConfirmDiscard}
        title="Discard changes?"
        description="You have unsaved edits to this project. Discarding closes the panel without saving."
        confirmLabel="Discard"
        destructive
        onConfirm={() => {
          setConfirmDiscard(false);
          setDirty(false);
          closeAndNotify();
        }}
      />

      <ConfirmDialog
        open={confirmCloseSave}
        onOpenChange={(o) => !o && !saving && setConfirmCloseSave(false)}
        title="Close this project?"
        description="Every field, phase, deliverable, team assignment, spec, budget, time entry, forecast and invoice on this project becomes read-only. Only Reopen can change it."
        confirmLabel="Close project"
        destructive
        busy={saving}
        onConfirm={() => void doSave()}
      />
    </>
  );
}
